/**
 * apiFetch — the admin's one CSRF rule. Same signature as `fetch`.
 *
 * The backend's CSRFMiddleware (server/src/middleware/csrf.py) is a
 * double-submit check:
 *
 *   - It runs on POST / PUT / PATCH / DELETE (GET, HEAD, OPTIONS are exempt),
 *     outside a short list of webhook / health paths.
 *   - It runs ONLY when the request carries the `access_token` cookie. A Bearer
 *     header does NOT exempt a request — the middleware never reads
 *     Authorization. The admin sends everything with `credentials: 'include'`,
 *     so whenever the browser holds that cookie for the API it is checked.
 *   - The `X-CSRF-Token` header must equal the `csrf_token` cookie, which the
 *     API sets JS-readable (utils/cookie_auth.py) next to the httpOnly
 *     `access_token` on login, register and refresh. Missing or different →
 *     403 {"detail": "CSRF token missing or invalid"}.
 *
 * Before this module the rule lived privately inside `useApi.apiCall`, and every
 * other transport — authService.makeAuthenticatedRequest (the timezone PATCH on
 * every page load), logout, login, token refresh, Etsy refresh/revoke, and
 * useApi.fetchFile's packing-slip / print-zip POSTs — skipped it and 403'd.
 * Now every admin request that can change state goes through here, and
 * src/services/__tests__/csrfFetchGuard.test.js fails the build on a new raw
 * `fetch` that sends a state-changing method.
 *
 * The token is read from the cookie on EVERY call, never cached: a login or
 * refresh rotates it, and a retried request must send the new value.
 *
 * It is only attached for our own API (a relative URL, API_BASE_URL's origin,
 * or this page's origin). It is a credential; a third-party host has no use
 * for it, and must not be handed one.
 */
import { API_BASE_URL } from '../constants/apiBase';

export const CSRF_COOKIE = 'csrf_token';
export const CSRF_HEADER = 'X-CSRF-Token';

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CSRF_COOKIE_PATTERN = new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]*)`);

export const isStateChangingMethod = method => STATE_CHANGING_METHODS.has(String(method || 'GET').toUpperCase());

/** The `csrf_token` cookie's value, or '' when there is none. */
export const getCsrfToken = () => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(CSRF_COOKIE_PATTERN);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const originOf = url => {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.href : undefined).origin;
  } catch {
    return null;
  }
};

const targetsOwnApi = input => {
  const url = typeof input === 'string' ? input : input?.url || String(input);
  // A path on this origin — relative, not protocol-relative.
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  const origin = originOf(url);
  if (!origin) return false;
  const pageOrigin = typeof window !== 'undefined' ? window.location.origin : null;
  return origin === originOf(API_BASE_URL) || origin === pageOrigin;
};

/** A plain-object copy of `headers` with `name` set to `value` (any existing casing replaced). */
const withHeader = (headers, name, value) => {
  const out = {};
  const copy = (v, k) => {
    if (k.toLowerCase() !== name.toLowerCase()) out[k] = v;
  };
  if (Array.isArray(headers)) headers.forEach(([k, v]) => copy(v, k));
  else if (headers && typeof headers.forEach === 'function') headers.forEach(copy);
  else Object.entries(headers || {}).forEach(([k, v]) => copy(v, k));
  out[name] = value;
  return out;
};

export const apiFetch = (input, init = {}) => {
  if (!isStateChangingMethod(init.method) || !targetsOwnApi(input)) {
    return fetch(input, init);
  }
  const token = getCsrfToken();
  if (!token) {
    return fetch(input, init);
  }
  return fetch(input, { ...init, headers: withHeader(init.headers, CSRF_HEADER, token) });
};

export default apiFetch;
